<template>
  <div class="report" id="c3">
    <ReportDraftWatermark v-if = "report.is_draft" />
    <div class="report-section">
      <v-simple-table dense>
        <tbody>
        <tr>
          <td>
            <span v-if="report.filer_name">{{ report.filer_name }}<br/></span>
            <span v-if="compareAndDisplayName">{{ committee.name }} - </span>
            <span class="registrationLink">(<a :href="report.committee_data.url" target="_blank">Registration</a>)<br/></span>
            {{ committee.address }}<br/>
            {{ committee.city }}, {{ committee.state }}
            {{ committee.postcode }}<br/>
            <span v-if="report.office">{{ report.office }}<br/>{{ report.jurisdiction }}</span>
          </td>
          <td>
            <div>Deposit date: {{ formatDate(report.user_data.period_start) }}</div>
            <div>Report number: {{ report.report_number }} <span
                v-if="report.amended_by_report">Amended by {{ report.amended_by_report }}</span><span
                v-if="report.amends_report">Amends {{ report.amends_report }}</span></div>
            <div>Submitted date: <span v-if="report.report_number">{{
                formatDate(report.user_data.submitted_at)
              }}</span></div>
          </td>
        </tr>
        </tbody>
      </v-simple-table>
    </div>
    <v-divider></v-divider>
    <div>
      <h3 class="line-item-title">1. Monetary contributions deposited in account</h3>
    </div>
    <v-divider></v-divider>

    <div>
      <v-simple-table dense>
        <thead>
        <tr>
          <th class="dateColumn">
            Date received
          </th>
          <th>
          </th>
          <th class="amountColumn">
            Amount
          </th>
          <th class="amountColumn">
            Total
          </th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td><span v-if="report.metadata.sums.anonymous.amount > 0">{{
              formatDate(report.metadata.sums.anonymous.date)
            }}</span></td>
          <td>a. Anonymous</td>
          <td class="amountColumn">{{ formatMoney(report.metadata.sums.anonymous.amount) }}</td>
          <td class="amountColumn">{{ formatMoney(report.metadata.sums.anonymous.aggregate) }}</td>
        </tr>
        <tr>
          <td><span v-if="report.metadata.sums.personal.amount > 0">{{
              formatDate(report.metadata.sums.personal.date)
            }}</span></td>
          <td>b. Candidate's personal funds deposited in the bank (include candidate loans in 1c)</td>
          <td class="amountColumn">{{ formatMoney(report.metadata.sums.personal.amount) }}</td>
          <td class="amountColumn">{{ formatMoney(report.metadata.sums.personal.aggregate) }}</td>
        </tr>
        <tr>
          <td>
            <span v-if="report.metadata.sums.loans.amount > 0 && !report.metadata.sums.loans.date">Various</span>
            <span v-else>{{ formatDate(report.metadata.sums.loans.date) }}</span>
          </td>
          <td>c. Loans, notes, security agreements (Schedule L)</td>
          <td class="amountColumn">{{ formatMoney(report.metadata.sums.loans.amount) }}</td>
          <td class="amountColumn"></td>
        </tr>
        <tr>
          <td>
            <span
                v-if="report.metadata.sums.misc_auction.amount > 0 && !report.metadata.sums.misc_auction.date">Various</span>
            <span v-else>{{ formatDate(report.metadata.sums.anonymous.date) }}</span>
          </td>
          <td>d. Miscellaneous receipts (interest, refunds, auctions, other)</td>
          <td class="amountColumn">{{ formatMoney(report.metadata.sums.misc_auction.amount) }}</td>
          <td class="amountColumn"></td>
        </tr>
        <tr>
          <td><span v-if="report.metadata.sums.small.amount > 0">{{
              formatDate(report.metadata.sums.small.date)
            }}</span></td>
          <td>e. Small contributions<span
              v-if="report.metadata.sums.small.count"> ({{ report.metadata.sums.small.count }} persons giving)</span></td>
          <td class="amountColumn">{{ formatMoney(report.metadata.sums.small.amount) }}</td>
          <td class="amountColumn"></td>
        </tr>
        </tbody>
      </v-simple-table>
    </div>

    <v-divider></v-divider>

    <div>
      <h3 class="line-item-title">2. Total funds received and deposited or credited to account:
        {{ formatMoney(totalFundsReceived) }}</h3>
      <div v-if="totalFundsReceived == 0">This report is an amendment to a previous report that was filed in error. No deposit was made.</div>
      <div class="small-text indented-text">Corresponds to amount on line 1, Schedule A to C4, and itemized below</div>
    </div>

    <div v-if="report.user_data.contributions">
      <v-divider></v-divider>
      <h3 class="line-item-title">3. Itemized contributions</h3>

      <v-simple-table dense>
        <thead>
        <tr>
          <th class="dateColumn">
            Date received
          </th>
          <th>
            Contributor<br/>(Name and address)
          </th>
          <th>
            Employer and address
          </th>
          <th class="amountColumn">
            Amount
          </th>
          <th class="amountColumn">
            Aggregate total
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, index) in report.user_data.contributions" :key="index">
          <td>{{ formatDate(item.date) }}</td>
          <td>
            <ContactInfo :contId="item.contact_key" :contacts="report.user_data.contacts"/>
          </td>
          <td>
            <EmployerInfo :contId="item.contact_key" :contacts="report.user_data.contacts"/>
          </td>
          <td class="amountColumn">{{ formatMoney(item.amount) }}</td>
          <td class="amountColumn">{{ electionFilter(item.election) }}<br/>{{
              formatMoney(getAggregateTotal(contacts, item.contact_key, item.election))
            }}
          </td>
        </tr>
        </tbody>
      </v-simple-table>
    </div>
    <v-divider></v-divider>
    <v-simple-table v-if="!report.is_draft" dense>
      <tbody>
      <tr>
        <td>
          <div class="small-text">Date of deposit</div>
          <div>{{ formatDate(report.user_data.period_start) }}</div>
        </td>
        <td>
          <div class="small-text"><strong>I certify this report is true and complete to the best of my
            knowledge</strong></div>
          <div class="small-text">Treasurer's signature and date</div>
          <div>{{ report.user_data.treasurer.name }} - {{ formatDate(report.receipt_date) }}</div>
        </td>
      </tr>
      </tbody>
    </v-simple-table>
  </div>
</template>

<script>
import ContactInfo from "./ContactInfo"
import EmployerInfo from "./EmployerInfo"
import filters from '../filters';
import utils from "./report-utils";
import ReportDraftWatermark from "@/components/ReportDraftWatermark";

export default {
  name: "C3Cover",
  created() {
    this.sortByDate(this.report.user_data.contributions);
  },
  data() {
    return {
      title: 'CASH RECEIPTS MONETARY CONTRIBUTIONS',
      aggregated: []
    }
  },
  components: {
    ContactInfo,
    EmployerInfo,
    ReportDraftWatermark
  },
  props: {
    report: {
      type: Object,
      default: function () {
        return {}
      }
    }
  },
  computed: {
    compareAndDisplayName() {
      return !this.report.filer_name || this.report.filer_name.trim() !== this.report.user_data.committee.name.trim();
    },
    committee() {
      return this.report.user_data.committee;
    },
    contacts() {
      return this.report.user_data.contacts;
    },
    totalFundsReceived() {
      return (this.checkExists(this.report.metadata.sums.itemized_contributions.amount) +
          this.checkExists(this.report.metadata.sums.anonymous.amount) +
          this.checkExists(this.report.metadata.sums.misc_auction.amount) +
          this.checkExists(this.report.metadata.sums.personal.amount) +
          this.checkExists(this.report.metadata.sums.small.amount) +
          this.checkExists(this.report.metadata.sums.loans.amount));
    },
  },
  methods: {
    ...filters.filters, ...utils,
    checkExists(item) {
      if (typeof (item) !== undefined) {
        return item
      } else {
        return 0;
      }
    },
  },
}
</script>
<style scoped>.report { position: relative; }</style>